/**
 * DeadlinesPanel
 * Panel showing all period deadlines (book-in, verify, submit)
 * Pattern: Organism - composes molecules and atoms to display deadline information
 */

import {
  Alert,
  Button,
  CheckCircleIcon,
  Chip,
  Divider,
  EventAvailableIcon,
  EventBusyIcon,
  EventIcon,
  PendingIcon,
  Skeleton,
  Text,
  Tooltip,
  cn,
} from '@billda/design-system';
import { format, differenceInDays } from 'date-fns';
import { useGetPotPeriods } from '../hooks/useGetPotPeriods';
import { PotPeriodSummaryDto } from '../api/potPeriodsApi';
import { useMemo, useState, useCallback } from 'react';
import { FeatureSwitchGate } from '../../../shared/components/gates/FeatureSwitchGate';
import { Features } from '@billda/features-ui';
import { TimeShiftDialog } from './TimeShiftDialog';
import { useDeadlineTimeShift } from '../hooks/useDeadlineTimeShift';
import { useEffectiveTime } from '../../development/hooks/useEffectiveTime';

interface DeadlinesPanelProps {
  currentYear: number;
}

interface DeadlineItemProps {
  label: string;
  deadline: string;
  icon: React.ReactNode;
  effectiveNow: Date;
  onGoWithin?: () => void;
  onPassDeadline?: () => void;
}

function DeadlineItem({
  label,
  deadline,
  icon,
  effectiveNow,
  onGoWithin,
  onPassDeadline,
}: DeadlineItemProps) {
  const deadlineDate = useMemo(() => new Date(deadline), [deadline]);
  const isOverdue = useMemo(
    () => deadlineDate < effectiveNow,
    [deadlineDate, effectiveNow]
  );
  const daysUntil = useMemo(
    () => differenceInDays(deadlineDate, effectiveNow),
    [deadlineDate, effectiveNow]
  );

  const getRelativeTimeText = () => {
    if (isOverdue) {
      const daysAgo = Math.abs(daysUntil);
      if (daysAgo === 0) {
        return 'today';
      }
      if (daysAgo === 1) {
        return '1 day ago';
      }
      return `${daysAgo} days ago`;
    }

    if (daysUntil === 0) {
      return 'today';
    }
    if (daysUntil === 1) {
      return 'tomorrow';
    }
    return `in ${daysUntil} days`;
  };

  const getDeadlineColorClass = () => {
    if (isOverdue) {
      return 'text-content-error';
    }
    if (daysUntil <= 1) {
      return 'text-content-warning';
    }
    return 'text-content-secondary';
  };

  return (
    <div className="flex items-start gap-3 py-2">
      <div className={cn('mt-1', getDeadlineColorClass())}>{icon}</div>
      <div className="flex-1">
        <Text variant="body2" className="font-medium">
          {label}
        </Text>
        <Text variant="body2" color="secondary">
          {format(deadlineDate, 'MMM d, yyyy • h:mm a')}
        </Text>
        <Text
          variant="caption"
          className={cn('font-medium', getDeadlineColorClass())}
        >
          {getRelativeTimeText()}
        </Text>
      </div>
      <FeatureSwitchGate feature={Features.Debug.IsTimeManipulationEnabled}>
        <div className="flex gap-1">
          <Tooltip content="Go within deadline">
            <span>
              <Button
                variant="ghost"
                size="icon"
                className="size-7"
                onClick={onGoWithin}
              >
                <EventAvailableIcon aria-hidden="true" className="size-4" />
              </Button>
            </span>
          </Tooltip>
          <Tooltip content="Pass deadline">
            <span>
              <Button
                variant="ghost"
                size="icon"
                className="size-7"
                onClick={onPassDeadline}
              >
                <EventBusyIcon aria-hidden="true" className="size-4" />
              </Button>
            </span>
          </Tooltip>
        </div>
      </FeatureSwitchGate>
    </div>
  );
}

interface TimeShiftDialogState {
  open: boolean;
  deadline: Date;
  mode: 'before' | 'after';
  label: string;
}

interface PeriodDeadlinesCardProps {
  period: PotPeriodSummaryDto;
  effectiveNow: Date;
}

function PeriodDeadlinesCard({
  period,
  effectiveNow,
}: PeriodDeadlinesCardProps) {
  const [dialogState, setDialogState] = useState<TimeShiftDialogState>({
    open: false,
    deadline: new Date(),
    mode: 'before',
    label: '',
  });

  const {
    shiftTime,
    isPending,
    isSettingTime,
    isTransitioning,
    isSuccess,
    reset,
  } = useDeadlineTimeShift();

  // Close dialog on success
  const handleClose = useCallback(() => {
    setDialogState(prev => ({ ...prev, open: false }));
    reset();
  }, [reset]);

  // Handle success - close dialog after brief delay
  if (isSuccess && dialogState.open) {
    setTimeout(() => {
      handleClose();
    }, 500);
  }

  const openDialog = useCallback(
    (deadline: string, mode: 'before' | 'after', label: string) => {
      setDialogState({
        open: true,
        deadline: new Date(deadline),
        mode,
        label,
      });
    },
    []
  );

  const handleConfirm = useCallback(
    (targetTime: Date) => {
      shiftTime(targetTime);
    },
    [shiftTime]
  );

  // Determine period status based on deadlines
  const periodStatus = useMemo(() => {
    const submitDeadline = new Date(period.submitByDeadline);
    if (submitDeadline < effectiveNow) {
      return 'completed';
    }
    return 'active';
  }, [period.submitByDeadline, effectiveNow]);

  return (
    <>
      <div className="rounded-lg border border-border-default bg-surface-default p-4">
        {/* Period Header */}
        <div className="mb-4 flex items-center justify-between">
          <div>
            <Text variant="h6" className="text-base font-semibold">
              Week {period.weekNumber} • {period.year}
            </Text>
            <Text variant="caption" color="secondary">
              {format(new Date(period.periodStart), 'MMM d')} –{' '}
              {format(new Date(period.periodEnd), 'MMM d, yyyy')}
            </Text>
          </div>
          {periodStatus === 'completed' ? (
            <Chip
              icon={<CheckCircleIcon aria-hidden="true" />}
              label="Complete"
              size="sm"
              color="success"
              variant="outlined"
            />
          ) : (
            <Chip
              icon={<PendingIcon aria-hidden="true" />}
              label="Active"
              size="sm"
              color="primary"
              variant="outlined"
            />
          )}
        </div>

        <Divider className="mb-4" />

        {/* Deadlines */}
        <div className="flex flex-col gap-2">
          <DeadlineItem
            label="Book In By"
            deadline={period.bookInByDeadline}
            icon={<EventIcon aria-hidden="true" className="size-4" />}
            effectiveNow={effectiveNow}
            onGoWithin={() =>
              openDialog(period.bookInByDeadline, 'before', 'Book In By')
            }
            onPassDeadline={() =>
              openDialog(period.bookInByDeadline, 'after', 'Book In By')
            }
          />
          <DeadlineItem
            label="Verify By"
            deadline={period.verifyByDeadline}
            icon={<EventIcon aria-hidden="true" className="size-4" />}
            effectiveNow={effectiveNow}
            onGoWithin={() =>
              openDialog(period.verifyByDeadline, 'before', 'Verify By')
            }
            onPassDeadline={() =>
              openDialog(period.verifyByDeadline, 'after', 'Verify By')
            }
          />
          <DeadlineItem
            label="Submit By"
            deadline={period.submitByDeadline}
            icon={<EventIcon aria-hidden="true" className="size-4" />}
            effectiveNow={effectiveNow}
            onGoWithin={() =>
              openDialog(period.submitByDeadline, 'before', 'Submit By')
            }
            onPassDeadline={() =>
              openDialog(period.submitByDeadline, 'after', 'Submit By')
            }
          />
        </div>
      </div>

      <TimeShiftDialog
        open={dialogState.open}
        onClose={handleClose}
        onConfirm={handleConfirm}
        deadline={dialogState.deadline}
        mode={dialogState.mode}
        deadlineLabel={dialogState.label}
        isPending={isPending}
        isSettingTime={isSettingTime}
        isTransitioning={isTransitioning}
      />
    </>
  );
}

export function DeadlinesPanel({ currentYear }: DeadlinesPanelProps) {
  const { data, isLoading, error } = useGetPotPeriods({
    year: currentYear,
    includeEmpty: true,
  });
  const { effectiveNow } = useEffectiveTime();

  // Loading state
  if (isLoading) {
    return (
      <div className="p-6">
        <div className="flex flex-col gap-4">
          <Skeleton variant="rectangular" height={200} />
          <Skeleton variant="rectangular" height={200} />
          <Skeleton variant="rectangular" height={200} />
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="p-6">
        <Alert severity="error">
          Failed to load period deadlines. Please try again.
        </Alert>
      </div>
    );
  }

  // No data state
  if (!data || data.periods.length === 0) {
    return (
      <div className="p-6">
        <Alert severity="info">No periods found for {currentYear}.</Alert>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex flex-col gap-6">
        {/* Header */}
        <div>
          <Text variant="h6" className="mb-2">
            All Deadlines
          </Text>
          <Text variant="body2" color="secondary">
            View booking, verification, and submission deadlines for all periods
            in {currentYear}.
          </Text>
        </div>

        {/* Period Cards */}
        <div className="flex flex-col gap-4">
          {data.periods.map(period => (
            <PeriodDeadlinesCard
              key={period.id}
              period={period}
              effectiveNow={effectiveNow}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
